import { all, call, takeLatest, put, select, takeLeading, takeEvery } from 'redux-saga/effects';
import { desksActions } from './desksSlice';
import { notificationHandler } from 'utils/utils';
import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import { axiosAiq } from 'config';
import { selectAllDesks, selectIsActiveDeskConfirmed, selectPendingSaturationChanges } from './desksSelectors';
import { selectUser } from 'store/authSlice/authSelectors';
import { selectActiveTab, selectIsReceivingTransactionsEnabled, selectIsVideoWallEnabled } from 'store/transactionsSlice/transactionsSelectors';
import { getSaturationCameras, getVideoWallCameras } from 'store/transactionsSlice/transactionSagas/transactionsSagas';
import { stompTopics } from 'workers/sharedWorker/utils/utils';
import {
    fetchLocationsWithGates,
    fetchSavedDesks,
    updateDeskSaturation,
    fetchDivisions,
    fetchDivisionById,
    fetchLocationsByDivision,
} from 'services/saturationService';
import { buildSaturationChangePayload } from 'utils/saturationChanges';
import { setIsDeskLoggingEnabled } from 'services/CameraStream/config';
import { ACTIVITY_END_REASON } from 'utils/activityTracking/constants';
import { snackbarActions } from 'store/snackbarsSlice/snackbarsSlice';

export function* fetchDesks() {
    try {
        const response = yield axiosAiq.get('/admin/desk-configuration');
        const {
            maxDeskTransactions,
            expectedFinishedTransactionMillis,
            circleCheckRotationIntervalMillis,
            maxStickyLanesPerDesk,
            videoWallCameraWarningLimit,
            totalStreamingCameraWarningLimit,
        } = response.data;
        yield put(desksActions.setMaxDeskTransactions(maxDeskTransactions));
        yield put(desksActions.setExpectedFinishedTransactionMillis(expectedFinishedTransactionMillis));
        yield put(desksActions.setCircleCheckRotationIntervalMillis(circleCheckRotationIntervalMillis));
        yield put(desksActions.setVideoWallCameraWarningLimit(videoWallCameraWarningLimit));
        yield put(desksActions.setTotalStreamingCameraWarningLimit(totalStreamingCameraWarningLimit));
        if (maxStickyLanesPerDesk != null) {
            yield put(transactionActions.setMaxPinnedLanes(maxStickyLanesPerDesk));
        }
    } catch (err) {
        yield call(notificationHandler, {
            err,
            action: desksActions.fetchDesksFailed,
            title: 'Error fetching desk configuration',
        });
    }

    try {
        const response = yield axiosAiq.get('/desks');
        const allDesks = response.data;
        yield put(desksActions.fetchDesksSuccess(allDesks));
        yield put(desksActions.checkDeskWarnings());
    } catch (err) {
        yield call(notificationHandler, {
            err,
            action: desksActions.fetchDesksFailed,
            title: 'Error fetching desks',
        });
    }
}

export function* fetchLocations() {
    try {
        const response = yield call(fetchLocationsWithGates);
        yield put(desksActions.fetchLocationsSuccess(response));
    } catch (err) {
        yield call(notificationHandler, {
            err,
            action: desksActions.fetchDesksFailed,
            title: 'Error fetching desk locations',
        });
    }
}

export function* fetchSavedDesk() {
    try {
        const response = yield call(fetchSavedDesks);
        yield put(desksActions.fetchSavedDesksSuccess(response));
    } catch (err) {
        yield call(notificationHandler, {
            err,
            action: desksActions.fetchSavedDesksFailed,
            title: 'Error fetching saved desks informations',
        });
    }
}

export function* fetchAllDivisions() {
    try {
        const response = yield call(fetchDivisions);
        yield put(desksActions.fetchDivisionsSuccess(response));
    } catch (err) {
        yield call(notificationHandler, {
            err,
            action: desksActions.fetchDivisionsFailed,
            title: 'Error fetching divisions',
        });
    }
}

export function* fetchDeskDivisionById({ payload }) {
    try {
        const response = yield call(fetchDivisionById, payload);
        yield put(desksActions.fetchDivisionByIdSuccess(response));
    } catch (err) {
        yield call(notificationHandler, {
            err,
            action: desksActions.fetchDivisionByIdFailed,
            title: 'Error fetching division by id',
        });
    }
}

export function* fetchDeskLocationByDivision({ payload }) {
    try {
        const response = yield call(fetchLocationsByDivision, payload);
        yield put(desksActions.fetchLocationByDivisionSuccess(response));
    } catch (err) {
        yield call(notificationHandler, {
            err,
            action: desksActions.fetchLocationByDivisionFailed,
            title: 'Error fetching locations by division',
        });
    }
}

export function* saveDeskAssignment() {
    const submittedChanges = yield select(selectPendingSaturationChanges);
    const changedDesks = buildSaturationChangePayload(submittedChanges);

    if (!changedDesks.length) return;

    yield put(desksActions.saveSaturationStart(submittedChanges));
    try {
        yield call(updateDeskSaturation, changedDesks);
        yield put(desksActions.saveSaturationSuccess());
        yield call(notificationHandler, {
            variant: 'success',
            title: 'Successfully saved desk saturation',
        });
    } catch (err) {
        yield put(desksActions.saveSaturationFailed());
        yield call(notificationHandler, {
            err,
            title: 'Error saving desk saturation, your changes were not saved',
        });
    }
}

export function* selectDesk({ payload: deskToSelect }) {
    const isActiveDeskConfirmed = yield select(selectIsActiveDeskConfirmed);
    if (!isActiveDeskConfirmed) return;

    const isTransactionPageActive = yield select(selectActiveTab);

    if (isTransactionPageActive) {
        yield put(transactionActions.unsubscribeFromStompBrokerTopic(stompTopics.transactionDesk));
        yield put(
            transactionActions.closeTransactionPanel({
                closeAllPanels: true,
                closeKurentoClient: false,
                // The agent is switching to a different desk, so the work is left behind rather
                // than completed (BEAST-13036).
                activityEndReason: ACTIVITY_END_REASON.CONTEXT_SWITCH,
            })
        );
    }

    // yield put(desksActions.setCurrentDeskInactive());
    yield put(desksActions.setCurrentDesk({}));
    // Circle Check is per-desk: always start a newly taken desk with it on.
    yield put(transactionActions.setCircleCheckActive(true));

    const { desk, takenBy } = yield call(isDeskAvailable, deskToSelect);

    if (desk) {
        yield put(desksActions.setIsDeskLoading(true));
        yield put(desksActions.setCurrentDesk(desk));

        const isReceivingTransactionsEnabled = yield select(selectIsReceivingTransactionsEnabled);
        yield put(desksActions.setIsActiveDeskConfirmed(!isReceivingTransactionsEnabled)); //false

        const isVideoWallEnabled = yield select(selectIsVideoWallEnabled);
        isVideoWallEnabled && (yield call(getVideoWallCameras, { payload: desk.id }));

        if (isReceivingTransactionsEnabled && isTransactionPageActive) {
            if (!isVideoWallEnabled) {
                yield call(getSaturationCameras, desk.id);
            }
            const { desk: deskStillAvailable } = yield call(isDeskAvailable, deskToSelect);
            if (deskStillAvailable) {
                yield put(transactionActions.subscribeToStompBrokerTopic({ topic: stompTopics.transactionDesk, url: `/topic/desks.${desk.id}` }));
            }
        }
        yield put(desksActions.setIsDeskLoading(false));
    } else {
        if (isTransactionPageActive && takenBy)
            yield call(notificationHandler, {
                title: `${deskToSelect.name.toUpperCase()} is taken${takenBy ? ` by ${takenBy}` : ''}, please select different desk.`,
            });
    }
}

export function* checkDeskWarnings() {
    try {
        const response = yield axiosAiq.get(`/desks/warnings`);

        const timeFormater = new Intl.DateTimeFormat(navigator.language, { hour: 'numeric', minute: 'numeric', second: 'numeric' });
        yield put(desksActions.setDeskWarnings({ deskWarnings: response.data, lastChecked: timeFormater.format(new Date()) }));
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: 'Error getting desk warnings',
        });
    }
}

export function* isDeskAvailable(deskToSelect) {
    const { username: activeUser } = yield select(selectUser);
    const allDesks = yield select(selectAllDesks);

    let takenBy;
    const desk = allDesks.find((desk) => {
        const hasSessions = !!desk.sessions && !!desk.sessions.length;
        const deskUser = desk.sessions?.[0]?.account?.username;
        const isAssignedToMe = hasSessions && !!deskUser && deskUser === activeUser;
        const isDeskToSelect = desk.id === deskToSelect.id;

        if (isDeskToSelect && hasSessions && !isAssignedToMe) takenBy = deskUser;

        return isDeskToSelect && (desk.status === 'INACTIVE' || isAssignedToMe);
    });

    return { desk, takenBy };
}

export function* setCurrentDesk({ payload: desk }) {
    yield call(setIsDeskLoggingEnabled, desk);
}

export function* onDesksFetchStart() {
    yield takeLatest(desksActions.fetchDesksStart.type, fetchDesks);
}

export function* onLocationsFetchStart() {
    yield takeLatest(desksActions.fetchLocationsStart.type, fetchLocations);
}

export function* onSavedDeskFetchStart() {
    yield takeLatest(desksActions.fetchSavedDeskStart.type, fetchSavedDesk);
}

export function* onDivisionsFetchStart() {
    yield takeLatest(desksActions.fetchDivisionsStart.type, fetchAllDivisions);
}

export function* onDivisionByIdFetchStart() {
    yield takeLatest(desksActions.fetchDivisionByIdStart.type, fetchDeskDivisionById);
}

export function* onLocationsByDivisionFetchStart() {
    yield takeLatest(desksActions.fetchLocationByDivisionStart.type, fetchDeskLocationByDivision);
}

export function* onSelectDesk() {
    yield takeLeading(desksActions.selectDesk.type, selectDesk);
}

export function* onSaveSaturation() {
    yield takeLeading(desksActions.saveDeskInfo.type, saveDeskAssignment);
}

export function* onCheckDeskWarnings() {
    yield takeEvery(desksActions.checkDeskWarnings.type, checkDeskWarnings);
}

export function* onSetCurrentDesk() {
    yield takeEvery(desksActions.setCurrentDesk.type, setCurrentDesk);
}

// Per-desk activity-tracking toggle (BEAST-13284). Optimistically flips the flag so the switch reacts
// immediately, PUTs it, and reverts with a snackbar on failure - mirrors the desk-pause handler.
export function* toggleDeskActivityEvents({ payload: { id, enabled } }) {
    yield put(desksActions.setDeskActivityEventsEnabled({ id, enabled }));
    try {
        yield axiosAiq.put(`/desks/${id}/activity-events-enabled`, null, { params: { enabled } });
    } catch {
        yield put(desksActions.setDeskActivityEventsEnabled({ id, enabled: !enabled }));
        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'ERROR UPDATING ACTIVITY TRACKING FOR DESK',
                options: { variant: 'error' },
            })
        );
    }
}

export function* onToggleDeskActivityEvents() {
    yield takeEvery(desksActions.toggleDeskActivityEventsEnabled.type, toggleDeskActivityEvents);
}

export function* desksSaga() {
    yield all([
        call(onDesksFetchStart),
        call(onSelectDesk),
        call(onCheckDeskWarnings),
        call(onLocationsFetchStart),
        call(onSavedDeskFetchStart),
        call(onSaveSaturation),
        call(onDivisionsFetchStart),
        call(onDivisionByIdFetchStart),
        call(onLocationsByDivisionFetchStart),
        call(onSetCurrentDesk),
        call(onToggleDeskActivityEvents),
    ]);
}
